"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _Divider = _interopRequireDefault(require("../Divider"));
var _ViewportProvider = require("../ViewportProvider");
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _renderFootnoteContent = _interopRequireDefault(require("./utils/renderFootnoteContent"));
var _renderPrice = _interopRequireDefault(require("./utils/renderPrice"));
var _renderTypography = _interopRequireDefault(require("./utils/renderTypography"));
var _jsxRuntime = require("react/jsx-runtime");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.htmlAttrs, _utils.viewProps]);
const selectTopTextTypographyTokens = _ref => {
  let {
    topTextFontSize,
    topTextLineHeight
  } = _ref;
  return {
    fontSize: topTextFontSize,
    lineHeight: topTextLineHeight
  };
};
const selectCurrencySymbolTypographyTokens = _ref2 => {
  let {
    currencySymbolFontSize,
    currencySymbolLineHeight,
    currencySymbolFontWeight
  } = _ref2;
  return {
    fontSize: currencySymbolFontSize,
    lineHeight: currencySymbolLineHeight,
    fontWeight: currencySymbolFontWeight
  };
};
const selectAmountTypographyTokens = _ref3 => {
  let {
    amountFontSize,
    amountLineHeight,
    amountLetterSpacing,
    amountFontWeight,
    fontColor
  } = _ref3;
  // This is used to apply the proper line height to the amount
  const lineHeightMultiplier = 1.18;
  return {
    color: fontColor,
    fontSize: amountFontSize,
    lineHeight: amountLineHeight * lineHeightMultiplier,
    letterSpacing: amountLetterSpacing,
    fontWeight: amountFontWeight
  };
};
const selectCentsTypographyTokens = _ref4 => {
  let {
    centsFontSize,
    centsLineHeight,
    centsFontWeight
  } = _ref4;
  return {
    fontSize: centsFontSize,
    lineHeight: centsLineHeight,
    fontWeight: centsFontWeight
  };
};
const selectRateTypographyTokens = _ref5 => {
  let {
    rateFontSize,
    rateLineHeight,
    rateFontWeight
  } = _ref5;
  return {
    fontSize: rateFontSize,
    lineHeight: rateLineHeight,
    fontWeight: rateFontWeight
  };
};
const selectBottomTextTypographyTokens = _ref6 => {
  let {
    bottomTextFontSize,
    bottomTextLineHeight
  } = _ref6;
  return {
    fontSize: bottomTextFontSize,
    lineHeight: bottomTextLineHeight
  };
};
const selectContainertokens = _ref7 => {
  let {
    alignItemsText
  } = _ref7;
  return {
    alignItems: alignItemsText
  };
};
const PriceLockup = /*#__PURE__*/_react.default.forwardRef((_ref8, ref) => {
  let {
    size = 'medium',
    signDirection = 'left',
    footnoteLinks,
    topText,
    price,
    currencySymbol = '$',
    rateText,
    ratePosition = 'right',
    bottomText,
    onClickFootnote,
    strikeThrough,
    a11yText,
    tokens: priceLockupTokens,
    variant = {},
    ...rest
  } = _ref8;
  const viewport = (0, _ViewportProvider.useViewport)();
  const {
    footnoteMarginTop,
    footnoteGap,
    bottomTextMarginTop,
    priceMarginBottom,
    bottomLinksMarginLeft,
    fontColor,
    dividerColor,
    ...themeTokens
  } = (0, _ThemeProvider.useThemeTokens)('PriceLockup', priceLockupTokens, {
    ...variant,
    size
  }, {
    viewport,
    strikeThrough
  });
  const currencySymbolTypographyTokens = selectCurrencySymbolTypographyTokens(themeTokens);
  const amountTypographyTokens = selectAmountTypographyTokens(themeTokens);
  const centsTypographyTokens = selectCentsTypographyTokens(themeTokens);
  const rateTypographyTokens = selectRateTypographyTokens(themeTokens);
  const topTextTypographyTokens = selectTopTextTypographyTokens(themeTokens);
  const bottomTextTypographyTokens = selectBottomTextTypographyTokens(themeTokens);
  return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_View.default, {
    style: [staticStyles.priceLockupContainer, selectContainertokens(themeTokens)],
    ref: ref,
    ...selectProps(rest),
    children: [topText ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
      style: staticStyles.topText,
      children: (0, _renderTypography.default)(topText, topTextTypographyTokens)
    }) : null, (0, _renderPrice.default)(price, rateText, ratePosition, signDirection, currencySymbol, currencySymbolTypographyTokens, amountTypographyTokens, centsTypographyTokens, rateTypographyTokens, fontColor, strikeThrough, a11yText, bottomText, bottomLinksMarginLeft, footnoteLinks, onClickFootnote, themeTokens), bottomText ? /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
      children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_Divider.default, {
        testID: "price-lockup-divider",
        role: "separator",
        tokens: {
          color: dividerColor
        }
      }), (0, _renderFootnoteContent.default)(footnoteMarginTop, bottomTextMarginTop, bottomText, bottomTextTypographyTokens, fontColor, footnoteLinks, bottomLinksMarginLeft, onClickFootnote, themeTokens)]
    }) : null]
  });
});
PriceLockup.displayName = 'PriceLockup';
PriceLockup.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Size of the component
   *
   * Small for pricing in product catalogue pages, medium for pricing in product comparison cards.
   */
  size: _propTypes.default.oneOf(['small', 'medium']),
  /**
   * If currency symbol other than `$` to be used
   */
  currencySymbol: _propTypes.default.string,
  /**
   * Shows additional info above the price
   */
  topText: _propTypes.default.string,
  /**
   * Monetary value (including decimals separated by ".")
   */
  price: _propTypes.default.string.isRequired,
  /**
   * Shows month/year unit
   */
  rateText: _propTypes.default.string,
  /**
   * Shows additional info below the price with a `Divider`
   */
  bottomText: _propTypes.default.string,
  /**
   * Displays which side the currency should appear (left, right)
   */
  signDirection: _propTypes.default.oneOf(['right', 'left']),
  /**
   * Displays where the rate should appear (bottom, right)
   */
  ratePosition: _propTypes.default.oneOf(['right', 'bottom']),
  /**
   * Shows additional link for context
   */
  footnoteLinks: _propTypes.default.arrayOf(_propTypes.default.oneOfType([_propTypes.default.number, _propTypes.default.string])),
  /**
   * Function to be called when a footnote link is clicked
   */
  onClickFootnote: _propTypes.default.func,
  /**
   * To show price savings comparison
   */
  strikeThrough: _propTypes.default.bool,
  /**
   * To provide a11y text for `PriceLockup` component
   *
   * **Note:** a11yText will override strikethrough price, so it must include price (ie. "was 50 dollars per month")
   */
  a11yText: _propTypes.default.string,
  /**
   * `PriceLockup` tokens
   */
  tokens: (0, _utils.getTokensPropType)('PriceLockup')
};
var _default = exports.default = PriceLockup;
const staticStyles = _StyleSheet.default.create({
  priceLockupContainer: {
    alignSelf: 'flex-start'
  },
  topText: {
    marginBottom: 4
  }
});